import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import LoadingSpinner from "@/components/LoadingSpinner";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useApi from "@/hooks/useApi";

interface SearchResult {
  title: string;
  snippet: string;
  contentType: string;
  sourceUrl?: string;
  score?: number;
}

interface ChatMessage {
  role: "user" | "result" | "system";
  query?: string;
  results?: SearchResult[];
  text?: string;
  timestamp: Date;
}

export default function AINewChat() {
  const { azureIndexes, loading: indexesLoading } = useAzureIndexes();
  const [selectedIndexId, setSelectedIndexId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toastAlert = useToastAlert();
  const api = useApi();

  // Auto-select first index when loaded
  useEffect(() => {
    if (azureIndexes.length > 0 && !selectedIndexId) {
      setSelectedIndexId(azureIndexes[0].id);
    }
  }, [azureIndexes]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function search() {
    const query = input.trim();
    if (!query || !selectedIndexId || loading) return;

    setInput("");
    setLoading(true);

    setMessages(prev => [...prev, {
      role: "user",
      query,
      timestamp: new Date()
    }]);

    try {
      const resp = await api.messaging.post("AzureIndexes/QueryIndex", {
        indexId: selectedIndexId,
        query
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        setMessages(prev => [...prev, {
          role: "system",
          text: err?.error ?? `Error ${resp.status}`,
          timestamp: new Date()
        }]);
        return;
      }

      const data = await resp.json();
      const results: SearchResult[] = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.value)
          ? data.value.map((v: { title?: string; content?: string; sourceUrl?: string; score?: number; ContentType?: string }) => ({
              title: v.title ?? "Result",
              snippet: v.content ?? "",
              contentType: v.ContentType ?? "text",
              sourceUrl: v.sourceUrl,
              score: v.score
            }))
          : [];

      if (results.length === 0) {
        setMessages(prev => [...prev, {
          role: "system",
          text: "No results found. Try a different query or check that content has been published to this index.",
          timestamp: new Date()
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: "result",
          results,
          timestamp: new Date()
        }]);
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Search failed — is the messaging API running?");
    } finally {
      setLoading(false);
    }
  }

  if (indexesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (azureIndexes.length === 0) {
    return (
      <div className="p-10 text-center max-w-md mx-auto">
        <h3 className="text-base font-semibold text-slate-800 mb-2">
          No knowledge base indexes yet
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          An index is where your knowledge base content is stored for AI search.
        </p>
        <Link
          to="/ai-new/indexes"
          className="inline-block bg-primary hover:bg-primary-active text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          Create your first index
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4 gap-3">
      {/* Index selector + title row */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Test index:</span>
        <select
          value={selectedIndexId ?? ""}
          onChange={e => {
            setSelectedIndexId(Number(e.target.value));
            setMessages([]);
          }}
          className="flex-1 max-w-xs border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {azureIndexes.map(idx => (
            <option key={idx.id} value={idx.id}>{idx.name}</option>
          ))}
        </select>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={() => setMessages([])}
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            Clear
          </button>
        )}
      </div>

      {/* Results area */}
      <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 py-10 text-sm">
            Ask a question to verify your content was indexed correctly.
          </div>
        )}
        {messages.map((msg, i) => {
          if (msg.role === "user") {
            return (
              <div key={i} className="flex justify-end">
                <div className="bg-primary text-white rounded-xl rounded-tr-none px-3 py-2 text-sm max-w-[70%]">
                  {msg.query}
                </div>
              </div>
            );
          }
          if (msg.role === "system") {
            return (
              <div key={i} className="flex justify-center">
                <div className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-3 py-1">
                  {msg.text}
                </div>
              </div>
            );
          }
          // result
          return (
            <div key={i} className="space-y-2">
              {msg.results?.map((r, j) => (
                <div key={j} className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-sm font-medium text-slate-800 truncate">{r.title}</span>
                    {r.score !== undefined && (
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">
                        {(r.score * 100).toFixed(0)}% match
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-3">{r.snippet}</p>
                  {r.sourceUrl && (
                    <a
                      href={r.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] text-primary hover:underline mt-1 block truncate"
                    >
                      {r.sourceUrl}
                    </a>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-xl px-3 py-2 flex items-center gap-2">
              <LoadingSpinner />
              <span className="text-slate-500 text-xs">Searching…</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border border-slate-200 rounded-xl bg-slate-50 focus-within:border-primary focus-within:bg-white transition-colors">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); search(); }
          }}
          placeholder="Ask a question to test what was indexed…"
          rows={2}
          disabled={loading || !selectedIndexId}
          className="w-full bg-transparent resize-none px-3 pt-2.5 pb-1 text-sm text-slate-800 placeholder-slate-400 outline-none disabled:opacity-40"
        />
        <div className="flex items-center justify-end px-2 pb-2">
          <button
            onClick={search}
            disabled={!input.trim() || loading || !selectedIndexId}
            className="bg-primary hover:bg-primary-active disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
}
